import { useEffect, useRef, useState } from 'react';

const STATS = [
  { value: 500, suffix: '+', label: 'Clients Served', labelAr: 'عميل تمت خدمتهم', icon: 'ri-team-line' },
  { value: 10, suffix: 'M+', label: 'Gloves Supplied', labelAr: 'قفاز تم تزويده', icon: 'ri-hand-sanitizer-line' },
  { value: 10, suffix: '+', label: 'Countries', labelAr: 'دولة', icon: 'ri-global-line' },
  { value: 24, suffix: 'hr', label: 'Response Time', labelAr: 'وقت الاستجابة', icon: 'ri-flashlight-line' },
];

function useCountUp(target: number, duration = 1800, start: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);

  return count;
}

function StatCard({ value, suffix, label, labelAr, icon }: (typeof STATS)[0]) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const count = useCountUp(value, 1600, visible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center text-center px-4 py-8 bg-white rounded-xl border border-gray-100 hover:border-brand-green/30 hover:shadow-md transition-all"
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-navy/8 mb-4">
        <i className={`${icon} text-2xl text-navy`} />
      </div>
      <div className={`font-heading font-black text-navy text-4xl md:text-5xl mb-1 ${visible ? 'count-animated' : 'opacity-0'}`}>
        {count.toLocaleString()}{suffix}
      </div>
      <p className="font-heading font-semibold text-gray-600 text-sm">{label}</p>
      <p className="font-arabic text-gray-600 text-xs mt-0.5" dir="rtl">{labelAr}</p>
    </div>
  );
}

export default function TrustStats() {
  return (
    <section className="section-padding bg-gray-50">
      <div className="container-max">
        <div className="text-center mb-10">
          <span className="trust-badge mb-4 inline-flex">🏅 Trusted Excellence in Oman</span>
          <h2 className="section-title-en text-3xl md:text-4xl mb-2">Numbers That Speak for Themselves</h2>
          <p className="section-title-ar text-2xl md:text-3xl" dir="rtl">أرقام تتحدث عن نفسها</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {STATS.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
}
